import type { PermissionKey } from "@/constants/permissions"
import useUser from "@/hooks/useUser"
import { useMemo } from "react"

export interface PermissionGuard {
  /** True when the current user has the permission (admins wildcard; kill-switched by TEAM_MANAGEMENT). */
  canRun: boolean
  /** The key that was checked — kept for debugging and analytics. */
  permission: PermissionKey
}

/**
 * Thin hook alias for `useUser().hasPermission(key)` — used by
 * `useGuardedMutation` and the rare component that needs to branch on a
 * permission without wrapping a whole subtree in `<PermissionGate>`.
 *
 * Prefer `<PermissionGate>` (hide) or `<RequirePermission>` (route redirect)
 * for the primary UX. Reach for this only when neither fits.
 */
export function usePermissionGuard(permission: PermissionKey): PermissionGuard {
  const { hasPermission } = useUser()
  const canRun = hasPermission(permission)
  return useMemo(() => ({ canRun, permission }), [canRun, permission])
}
